import { deg2rad } from "../math/angles";
import { fmt } from "../format/number";
import type { Section } from "../geometry/section";
import type { WallInput } from "../types/input";
import type { EcCheck, EcCombinationResult, Force } from "../types/result";
import { assembleCase, type AssembleContext, type CaseParams } from "../loads/assemble";
import { baseFrame, baseFrictionAngle, resolveBearingValues, type Warn } from "../checks/stability";
import { bearingCapacity16_8a } from "../checks/bearing";
import type { EcCombination } from "./ec";

const factorAngleDeg = (deg: number, g: number): number => (Math.atan(Math.tan(deg2rad(deg)) / g) * 180) / Math.PI;

/** Malzeme kısmi katsayıları uygulanmış girdi kopyası (tanφ/γφ, c/γc; δ ve taban sürtünmesi de türev olarak). */
export function factoredInput(input: WallInput, gPhi: number, gC: number, surcharge: number, lineLoads: boolean): WallInput {
  const w = structuredClone(input);
  w.backfill.layers = w.backfill.layers.map((L) => ({ ...L, phi: factorAngleDeg(L.phi, gPhi), c: L.c / gC }));
  if (typeof w.backfill.wallFrictionAngle === "number") w.backfill.wallFrictionAngle = factorAngleDeg(w.backfill.wallFrictionAngle, gPhi);
  w.foundationSoil.phi = factorAngleDeg(w.foundationSoil.phi, gPhi);
  w.foundationSoil.c = w.foundationSoil.c / gC;
  w.foundationSoil.adhesion = w.foundationSoil.adhesion / gC;
  if (w.foundationSoil.baseFriction.mode === "custom" && w.foundationSoil.baseFriction.value !== undefined) w.foundationSoil.baseFriction.value = factorAngleDeg(w.foundationSoil.baseFriction.value, gPhi);
  w.loads.surcharge = surcharge;
  if (!lineLoads) w.loads.lineLoads = [];
  return w;
}

/** Kuvvetleri kalıcı-lehte / kalıcı-aleyhte ayırır (sanal düzlem yöntemi: W ve kama lehte; itki, su, atalet aleyhte). */
function split(forces: Force[]): { Mr: number; Mo: number; V: number; U: number; H: number; Rpk: number; Hres: number; PavMr: number; Pav: number } {
  let Mr = 0, Mo = 0, V = 0, U = 0, H = 0, Rpk = 0, Hres = 0, PavMr = 0, Pav = 0;
  for (const f of forces) {
    if (f.kind === "passive") { Rpk += -f.H; continue; }
    if (f.kind === "frontWater") { Hres += -f.H; continue; }
    if (f.kind === "earthStatic" || f.kind === "earthDynamic") { Pav += Math.max(f.V, 0); PavMr += Math.max(f.V, 0) * f.armX; }
    else { V += Math.max(f.V, 0); Mr += f.Mres; }
    U += Math.max(-f.V, 0);
    H += Math.max(f.H, 0);
    Mo += f.Movr;
  }
  return { Mr, Mo, V, U, H, Rpk, Hres, PavMr, Pav };
}

const check = (Ed: number, Rd: number, note: string): EcCheck => ({ Ed, Rd, ratio: Rd > 1e-12 ? Ed / Rd : Infinity, ok: Ed <= Rd + 1e-9, note });

/**
 * EN 1997-1 kısmi katsayılı doğrulama. Her kombinasyon için: malzeme katsayıları uygulanmış girdiyle yükler yeniden kurulur;
 * kalıcı etkiler γG (aleyhte) / γG,fav (lehte), değişken (sürşarj, şerit/çizgisel yük) γQ (lehte iken 0) ile çarpılır.
 * Tek kaynak ilkesi: itkinin düşey bileşeni Pav kayma ve devrilmede lehte (γG,fav), taşımada aleyhte (γG) alınır.
 */
export function verifyEc(input: WallInput, section: Section, cp: CaseParams, combos: EcCombination[], warn: Warn): EcCombinationResult[] {
  const out: EcCombinationResult[] = [];
  const silent: Warn = () => undefined;
  for (const cb of combos) {
    const f = cb.f;
    const inP = factoredInput(input, f.gPhi, f.gC, 0, false);
    const inQ = factoredInput(input, f.gPhi, f.gC, input.loads.surcharge, true);
    const mk = (w: WallInput): AssembleContext => ({ input: w, section, origin: section.toe, includeFoundationEffects: true, warn: silent });
    const P = split(assembleCase(mk(inP), cp).forces);
    const Q = split(assembleCase(mk(inQ), cp).forces);
    const dMo = Q.Mo - P.Mo, dH = Q.H - P.H, dV = Q.V - P.V, dMr = Q.Mr - P.Mr, dPav = Q.Pav - P.Pav, dPavMr = Q.PavMr - P.PavMr;
    const fs = inP.foundationSoil;
    // Eğik taban (tanα = section.baseSlope): kayma/taşıma taban düzlemine izdüşürülür (checks/stability.ts baseFrame); yatayda cos = 1, sin = 0
    const slope = section.baseSlope;
    const { cos, sin } = baseFrame(slope);
    const B = section.B / cos;

    // EQU / devrilme: aleyhte γG (itki, su, atalet) + γQ·ΔMo ; lehte γG,fav (ağırlıklar, Pav); değişken lehte 0
    const Mdst = f.gG * P.Mo + f.gQ * dMo;
    const Mstb = f.gGfav * (P.Mr + P.PavMr);
    const overturning = check(Mdst, Mstb, `M_dst,d = ${fmt(f.gG, 2)}·${fmt(P.Mo)} + ${fmt(f.gQ, 2)}·${fmt(dMo)} = ${fmt(Mdst)} ≤ M_stb,d = ${fmt(f.gGfav, 2)}·${fmt(P.Mr + P.PavMr)} = ${fmt(Mstb)} kNm/m`);

    // GEO / kayma: Hd = γG·H + γQ·ΔH ; Nd = γG,fav·(V + Pav) − γG·U ; Rd = Nd·tanδd/γRh + Rp,d/γRe + ön su
    const Hh = f.gG * P.H + f.gQ * dH;
    const Nv = f.gGfav * (P.V + P.Pav) - f.gG * P.U;
    const Hd = Hh * cos - Nv * sin;
    const Nd = Nv * cos + (Hh - P.Rpk - P.Hres) * sin;
    const tanD = Math.tan(baseFrictionAngle(fs));
    const RhChar = fs.drained ? Nd * tanD + fs.adhesion * B : B * fs.c;
    const Rd = RhChar / f.gRh + (P.Rpk * cos) / f.gRe + P.Hres * cos;
    const sliding = check(Hd, Rd, `H_d = ${fmt(Hd)} ≤ R_d = ${fmt(RhChar)}/${fmt(f.gRh, 2)} + ${fmt(P.Rpk * cos)}/${fmt(f.gRe, 2)} = ${fmt(Rd)} kN/m (tanδ_d = ${fmt(tanD, 3)}${slope > 0 ? `; eğik taban: H_d = T'_d, N'_d = ${fmt(Nd)}` : ""})`);

    // GEO / taşıma: düşeyler aleyhte γG; değişken γQ; e ve B' tasarım değerleriyle
    const Vv = f.gG * (P.V + P.Pav + dV + dPav) - f.gG * P.U;
    const Vd = Vv * cos + Hh * sin;
    const Hb = slope > 0 ? Math.abs(Hh * cos - Vv * sin) : Hh;
    const Mrd = f.gG * (P.Mr + P.PavMr + dMr + dPavMr);
    const Mod = f.gG * P.Mo + f.gQ * dMo;
    const xbar = Vd > 1e-9 ? (Mrd - Mod) / Vd : 0;
    const eD = B / 2 - xbar;
    const BeffD = Math.max(0, B - 2 * Math.abs(eD));
    const qEd = BeffD > 1e-9 ? Vd / BeffD : Infinity;
    let qRd: number, bnote: string;
    if (fs.bearing.mode === "computed") {
      const submerged = input.water.frontLevel !== null && input.water.frontLevel > 0;
      const g = submerged ? fs.gamma - input.water.gammaWater : fs.gamma;
      const bc = bearingCapacity16_8a({ c: fs.c, phi: deg2rad(fs.phi), gammaBelow: g, gammaAbove: g, Df: input.geometry.embedmentDepth, Beff: BeffD, N: Vd, H: Hb });
      qRd = bc.qk / f.gRv;
      bnote = `q_Ed = V_d/B' = ${fmt(Vd)}/${fmt(BeffD, 3)} = ${fmt(qEd)} ≤ R_d = q_ult(φ_d, c_d)/γRv = ${fmt(bc.qk)}/${fmt(f.gRv, 2)} = ${fmt(qRd)} kN/m²`;
    } else {
      const { qk } = resolveBearingValues(fs, silent);
      qRd = qk / f.gRv;
      bnote = `q_Ed = V_d/B' = ${fmt(Vd)}/${fmt(BeffD, 3)} = ${fmt(qEd)} ≤ R_d = q_k/γRv = ${fmt(qk)}/${fmt(f.gRv, 2)} = ${fmt(qRd)} kN/m²`;
    }
    const bearing = check(qEd, qRd, bnote);
    if (Math.abs(eD) > B / 3) warn("EC7_ECCENTRICITY", cb.id);

    out.push({ id: cb.id, label: cb.label, limitState: cb.limitState, factors: { ...f }, phiD: inP.backfill.layers[0]!.phi, cD: inP.backfill.layers[0]!.c, overturning, sliding, bearing, Vd, Hd, eD, BeffD });
  }
  return out;
}

/** Kombinasyonlar arasında en büyük oranı (hükmeden) verir. */
export function governing(results: EcCombinationResult[], key: "overturning" | "sliding" | "bearing"): EcCombinationResult {
  return results.reduce((a, b) => (b[key].ratio > a[key].ratio ? b : a));
}

